import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Menu,
  X,
  Search,
  ShoppingCart,
  Home,
  Car,
  Wrench,
  MapPin,
  ShieldCheck,
  PanelLeftClose,
  PanelLeft,
  Coins,
  LayoutGrid,
  Grid3x3,
  List,
  Wallet,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { CartDrawer } from '@/components/Cart/CartDrawer'
import { useCartState } from '@/hooks/context/global/useCartState'
import { useRepuestosLayout } from '@/hooks/context/global/useRepuestosLayout'
import { useCatalogPrefs } from '@/hooks/context/global/useCatalogPrefs'

/**
 * Header de las secciones "app" (Catálogo de vehículos y Repuestos).
 * En móvil: barra sólida con menú, conmutador Repuestos/Vehículos y carrito.
 * En escritorio: conmutador + logo central + navegación.
 */
export function RepuestosHeader() {
  const [cartOpen, setCartOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const location = useLocation()
  const cartCount = useCartState((s) => s.totalItems())
  const { sidebarCollapsed, toggleSidebar, searchOpen, toggleSearch } = useRepuestosLayout()
  const { currency, setCurrency, viewMode, setViewMode, showInitialPrice, setShowInitialPrice } =
    useCatalogPrefs()
  const isRepuestosPage = location.pathname === '/repuestos'
  const isCatalogoPage = location.pathname === '/catalogo'
  const hasAppSidebar = isRepuestosPage || isCatalogoPage

  /** Botón de un toggle segmentado dentro del menú oscuro. */
  const SegmentButton = ({
    active,
    onClick,
    children,
    label,
  }: {
    active: boolean
    onClick: () => void
    children: React.ReactNode
    label: string
  }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-lg px-1.5 py-2 text-sm font-semibold transition-colors ${
        active ? 'bg-[#E10F18] text-white shadow-sm' : 'text-white/70 hover:text-white'
      }`}
    >
      {children}
    </button>
  )

  /** Sección "Visualización" — preferencias de catálogo/repuestos. */
  const DisplayPrefs = () => (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
      <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-yellow-200/90">
        Visualización
      </p>
      <div className="space-y-3">
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-medium text-white/70">
            <Coins className="h-3.5 w-3.5" /> Moneda
          </p>
          <div className="flex gap-1 rounded-xl bg-black/25 p-1">
            <SegmentButton active={currency === 'DOP'} onClick={() => setCurrency('DOP')} label="Mostrar todos los precios en pesos dominicanos">
              RD$
            </SegmentButton>
            <SegmentButton active={currency === 'USD'} onClick={() => setCurrency('USD')} label="Mostrar todos los precios en dólares">
              US$
            </SegmentButton>
            <SegmentButton active={currency === 'ORIGINAL'} onClick={() => setCurrency('ORIGINAL')} label="Mostrar cada precio en su moneda original">
              Original
            </SegmentButton>
          </div>
        </div>

        <div>
          <p className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-medium text-white/70">
            <LayoutGrid className="h-3.5 w-3.5" /> Vista
          </p>
          <div className="flex gap-1 rounded-xl bg-black/25 p-1">
            <SegmentButton active={viewMode === 'grid'} onClick={() => setViewMode('grid')} label="Vista en tarjetas grandes">
              <LayoutGrid className="h-4 w-4" />
              <span className="text-xs">Tarjetas</span>
            </SegmentButton>
            <SegmentButton active={viewMode === 'compact'} onClick={() => setViewMode('compact')} label="Vista en cuadrícula compacta">
              <Grid3x3 className="h-4 w-4" />
              <span className="text-xs">Cuadrícula</span>
            </SegmentButton>
            <SegmentButton active={viewMode === 'list'} onClick={() => setViewMode('list')} label="Vista en lista">
              <List className="h-4 w-4" />
              <span className="text-xs">Lista</span>
            </SegmentButton>
          </div>
        </div>

        {isCatalogoPage && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-medium text-white/70">
              <Wallet className="h-3.5 w-3.5" /> Precio
            </p>
            <div className="flex gap-1 rounded-xl bg-black/25 p-1">
              <SegmentButton active={!showInitialPrice} onClick={() => setShowInitialPrice(false)} label="Mostrar precio total">
                Total
              </SegmentButton>
              <SegmentButton active={showInitialPrice} onClick={() => setShowInitialPrice(true)} label="Mostrar precio inicial">
                Inicial
              </SegmentButton>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  /** Botón que abre/cierra el buscador del catálogo. */
  const SearchButton = ({ size = 'h-10 w-10' }: { size?: string }) => (
    <button
      type="button"
      onClick={toggleSearch}
      aria-label={searchOpen ? 'Cerrar búsqueda' : 'Buscar'}
      aria-pressed={searchOpen}
      className={`inline-flex ${size} shrink-0 items-center justify-center rounded-full border transition-colors ${
        searchOpen
          ? 'border-[#E10F18] bg-[#E10F18] text-white'
          : 'border-white/20 bg-white/10 text-white hover:bg-white/20'
      }`}
    >
      <Search className="h-5 w-5" />
    </button>
  )

  /** Conmutador Repuestos / Vehículos */
  const SectionToggle = ({ compact = false }: { compact?: boolean }) => (
    <div className="flex items-center rounded-full bg-white/10 p-1">
      <Link
        to="/repuestos"
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors ${
          compact ? 'px-3.5 py-1.5 text-sm' : 'px-4 py-1.5 text-sm'
        } ${
          isRepuestosPage
            ? 'bg-[#E10F18] text-white'
            : 'text-white/80 hover:text-white'
        }`}
      >
        Repuestos
      </Link>
      <Link
        to="/catalogo"
        className={`rounded-full font-semibold transition-colors ${
          compact ? 'px-3.5 py-1.5 text-sm' : 'px-4 py-1.5 text-sm'
        } ${
          isCatalogoPage
            ? 'bg-[#E10F18] text-white'
            : 'text-white/80 hover:text-white'
        }`}
      >
        Vehículos
      </Link>
    </div>
  )

  return (
    <header
      className={`fixed top-0 right-0 z-30 h-16 overflow-visible border-b border-white/10 bg-[#1F2240] shadow-lg transition-[left] duration-300 lg:h-20 ${
        hasAppSidebar && !sidebarCollapsed ? 'left-0 lg:left-80' : 'left-0'
      }`}
    >
      {/* ===== MÓVIL ===== */}
      <div className="flex h-full items-center gap-2 px-3 lg:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label="Abrir menú de navegación"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors active:bg-white/25"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="flex flex-1 items-center justify-center">
          <SectionToggle compact />
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {isRepuestosPage && (
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              aria-label="Ver carrito"
              className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors active:bg-white/25"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E10F18] px-1 text-[10px] font-bold text-white">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>
          )}
          <SearchButton size="h-11 w-11" />
        </div>
      </div>

      {/* ===== ESCRITORIO ===== */}
      <div className="hidden h-full items-center justify-between gap-4 px-6 lg:flex">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {hasAppSidebar && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full text-white hover:bg-white/10"
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed ? 'Mostrar filtros' : 'Ocultar filtros'}
            >
              {sidebarCollapsed ? (
                <PanelLeft className="h-5 w-5" />
              ) : (
                <PanelLeftClose className="h-5 w-5" />
              )}
            </Button>
          )}
          <SectionToggle />
        </div>

        {/* Logo central que sobresale */}
        <div className="pointer-events-none absolute left-1/2 top-0 flex h-full -translate-x-1/2 items-center justify-center">
          <Link
            to="/"
            className="pointer-events-auto -my-4 mt-8 block transition-transform duration-300 hover:scale-105"
            aria-label="Inicio"
          >
            <span className="relative flex h-28 w-28 shrink-0 items-center justify-center rounded-full ring-4 ring-[#E10F18] shadow-[0_8px_24px_rgba(225,15,24,0.35)]">
              <img
                src="/logo.png"
                alt="Logo Oriental Ramirez"
                className="h-full w-full rounded-full object-cover"
              />
            </span>
          </Link>
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          <nav className="flex items-center gap-1">
            <Link to="/" className="rounded-lg px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white">
              Inicio
            </Link>
            <Link to="/sucursales" className="rounded-lg px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white">
              Sucursales
            </Link>
            <Link to="/garantia" className="rounded-lg px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white">
              Garantía
            </Link>
          </nav>
          {isRepuestosPage && (
            <Button
              variant="ghost"
              size="icon"
              className="relative h-10 w-10 shrink-0 rounded-full text-white hover:bg-white/10"
              onClick={() => setCartOpen(true)}
              aria-label="Ver carrito"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#E10F18] text-[10px] font-bold text-white">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Button>
          )}
          <SearchButton />
        </div>
      </div>

      {/* ===== Menú lateral (móvil) ===== */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="left"
          className="w-[300px] border-r border-white/10 bg-[#1F2240] p-0 [&>button]:hidden"
        >
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
              <Link to="/" onClick={() => setSheetOpen(false)} className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white ring-2 ring-[#E10F18]">
                  <img src="/logo.png" alt="" className="h-full w-full rounded-full object-cover p-0.5" />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-bold text-white">Oriental Ramirez</p>
                  <p className="text-[11px] text-yellow-200/80">Vehículos y repuestos</p>
                </div>
              </Link>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Cerrar menú"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors active:bg-white/10"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-3">
              <div className="grid gap-1">
                {[
                  { label: 'Inicio', icon: Home, to: '/' },
                  { label: 'Catálogo de vehículos', icon: Car, to: '/catalogo' },
                  { label: 'Repuestos', icon: Wrench, to: '/repuestos' },
                  { label: 'Sucursales', icon: MapPin, to: '/sucursales' },
                  { label: 'Garantía', icon: ShieldCheck, to: '/garantia' },
                ].map((item) => {
                  const Icon = item.icon
                  const active = location.pathname === item.to
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setSheetOpen(false)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-semibold transition-colors ${
                        active ? 'bg-[#E10F18] text-white' : 'text-white/85 active:bg-white/10'
                      }`}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                    </Link>
                  )
                })}
              </div>

              {hasAppSidebar && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <DisplayPrefs />
                </div>
              )}
            </nav>

            {isRepuestosPage && (
              <div className="border-t border-white/10 p-3">
                <button
                  type="button"
                  onClick={() => {
                    setSheetOpen(false)
                    setCartOpen(true)
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#E10F18] px-4 py-2.5 text-sm font-semibold text-white transition-colors active:bg-[#c00d15]"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Ver carrito
                  {cartCount > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/25 px-1.5 text-xs font-bold">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  )
}
